import { useEffect, useState, type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { Modal, useToast } from 'grabbitbot-legacy-ui'
import { tradingClient } from '../../api'
import type { CoinConfig, ConstantEntry, QuoteAsset, Strategy } from '../../api/tradingTypes'
import {
  LOGIC_LABELS,
  MAX_MOP_LABELS,
  TRADING_MODES,
  getConstantLabel,
  getFieldLabel,
  settingsDefaultAutoBuyAmountLabel,
  settingsDefaultAutoBuyAssetLabel,
  settingsDefaultAutoBuyModeLabel,
} from './coinConfigLabels'
import {
  addConstantOverride,
  buildCoinSettingsBody,
  initCoinSettingsForm,
  offModeTriggerForType,
  valueForConstantChoice,
  type CoinSettingsForm,
  type ConstantOverride,
} from './coinSettingsForm'
import { AUTO_BUY_MODES, OFF_MODE_TRIGGERS, PRIMARY_USD_ASSET, USD_ASSETS, isUsd } from './tradingConstants'
import { failureText } from '../../api/errorMessages'

/** `QuoteAssetService.getDefaultQuoteAssetList()[0]` — the fallback range. */
const DEFAULT_QUOTE_ASSET: QuoteAsset = {
  coin2: PRIMARY_USD_ASSET,
  bid: { min: 15, max: 10000, step: 1 },
  grid: { min: 0.1, max: 10, step: 0.1 },
  coins: [],
  used: 1,
}

function traderField(config: CoinConfig, field: string): unknown {
  const trader = config.trader
  return trader && !Array.isArray(trader) ? (trader as Record<string, unknown>)[field] : undefined
}
function traderConstants(config: CoinConfig, field: 'bidConstants' | 'gridConstants'): ConstantEntry[] {
  const value = traderField(config, field)
  return Array.isArray(value) ? (value as ConstantEntry[]) : []
}
function defaultConstants(config: CoinConfig, field: 'bidConstants' | 'gridConstants'): ConstantEntry[] {
  const value = config.default?.[field]
  return Array.isArray(value) ? value : []
}

function FieldRow({ label, hint, children }: { label: ReactNode; hint?: ReactNode; children?: ReactNode }) {
  return (
    <div className="portal-setting-row">
      <div className="portal-setting-row__label">
        {label}
        {hint && <span className="portal-muted-small">{hint}</span>}
      </div>
      <div className="portal-setting-row__value">{children}</div>
    </div>
  )
}

/**
 * Equivalent of legacy `V3CoinSettingsPopupComponent` (opened from the coin
 * list and the strategy sheet): edit one coin pair's config. The state and
 * the exact PATCH body live in `coinSettingsForm.ts` (pure, unit tested); this
 * component only renders it. An own config (`syncState == 0`) edits
 * bid/grid/trading mode/lock; a subscribed pair only its constant overrides
 * and the advanced fields — same split as legacy.
 *
 * Difference from legacy: a failed save shows an error and re-enables Save
 * (legacy had no error branch and stayed "saving" forever).
 */
export function CoinSettingsPopup({
  coinConfig,
  onClose,
}: {
  coinConfig: CoinConfig | null
  onClose: (refresh: boolean) => void
}) {
  const { showToast } = useToast()
  const [form, setForm] = useState<CoinSettingsForm | null>(null)
  const [quoteAsset, setQuoteAsset] = useState<QuoteAsset>(DEFAULT_QUOTE_ASSET)
  const [strategy, setStrategy] = useState<Strategy | null>(null)
  const [showAdvanced, setShowAdvanced] = useState(false)
  const [saving, setSaving] = useState(false)
  const [newBid, setNewBid] = useState({ name: '', value: 0 })
  const [newGrid, setNewGrid] = useState({ name: '', value: 0 })

  useEffect(() => {
    setForm(coinConfig ? initCoinSettingsForm(coinConfig) : null)
    setStrategy(null)
    setShowAdvanced(false)
    if (!coinConfig) return
    let cancelled = false
    const exchangeId = coinConfig.exchangeId ?? ''
    if (exchangeId) {
      tradingClient
        .searchQuoteAssets(exchangeId)
        .then((list) => {
          if (cancelled) return
          setQuoteAsset((Array.isArray(list) ? list : []).find((q) => q.coin2 === coinConfig.coin2) ?? DEFAULT_QUOTE_ASSET)
        })
        .catch(() => undefined)
    }
    if (coinConfig.strategyId) {
      tradingClient
        .viewStrategy(coinConfig.strategyId)
        .then((s) => {
          if (!cancelled) setStrategy(s)
        })
        .catch(() => undefined)
    }
    return () => {
      cancelled = true
    }
  }, [coinConfig])

  if (!coinConfig || !form) return null
  const config = coinConfig
  const effective = config.effective ?? {}
  const own = form.syncState === 0
  const set = (patch: Partial<CoinSettingsForm>) => setForm((prev) => (prev ? { ...prev, ...patch } : prev))
  const setOverride = (field: 'bidConstants' | 'gridConstants', name: string, patch: Partial<ConstantOverride>) =>
    setForm((prev) => {
      if (!prev) return prev
      const current = prev[field][name] ?? { value: -1, customValue: 0 }
      return { ...prev, [field]: { ...prev[field], [name]: { ...current, ...patch } } }
    })

  async function save() {
    if (!form) return
    setSaving(true)
    try {
      await tradingClient.modifyCoinConfig(config.exchangeId ?? '', config.coin1, config.coin2, buildCoinSettingsBody(form))
      onClose(true)
    } catch (err) {
      showToast(failureText(err, 'Could not save the coin settings'), 'error')
    } finally {
      setSaving(false)
    }
  }

  const possibleModes = Array.isArray(effective.possibleTradingModes) ? effective.possibleTradingModes : []
  const currentModeListed = possibleModes.some((m) => String(m) === String(form.tradingModeValue))

  function constantOverrideSelect(
    field: 'bidConstants' | 'gridConstants',
    name: string,
    defaultKey: string,
    postfix: string,
    range: { min: number; max: number; step: number },
  ) {
    const entry = form?.[field][name] ?? { value: -1, customValue: 0 }
    return (
      <span className="portal-input-group">
        <select
          aria-label={`${name} value source`}
          value={String(entry.value)}
          onChange={(e) => setOverride(field, name, { value: Number(e.target.value) })}
        >
          {(form!.syncState > 0 || entry.value === -2) && (
            <option value="-2">
              Use trader&apos;s value ({getConstantLabel(name, traderConstants(config, field), postfix)})
            </option>
          )}
          <option value="-1">
            Use my default value ({getConstantLabel(defaultKey, defaultConstants(config, field), postfix)})
          </option>
          <option value="-100">Custom value:</option>
        </select>
        {entry.value === -100 && (
          <input
            type="number"
            aria-label={`${name} custom value`}
            min={range.min}
            max={range.max}
            step={range.step}
            value={String(entry.customValue)}
            onChange={(e) => setOverride(field, name, { customValue: e.target.value })}
          />
        )}
      </span>
    )
  }

  function rawOrConstant(
    constantField: 'bidConstant' | 'gridUpConstant' | 'gridDownConstant',
    valueField: 'bid' | 'gridUp' | 'gridDown',
    constants: ConstantEntry[] | undefined,
    unit: string,
    range: { min: number; max: number; step: number },
    label: string,
  ) {
    const effectiveValue = effective[valueField]
    const hasConstants = Array.isArray(constants) && constants.length > 0
    return (
      <span className="portal-input-group">
        {hasConstants && (
          <select
            aria-label={`${label} constant`}
            value={form![constantField]}
            onChange={(e) => {
              const key = e.target.value
              set({
                [constantField]: key,
                [valueField]: valueForConstantChoice(key, constants, effectiveValue),
              } as Partial<CoinSettingsForm>)
            }}
          >
            {constants!.map((c) => (
              <option key={String(c.key)} value={String(c.key)}>
                {String(c.key)} ({String(c.value)}
                {unit === '%' ? '%' : ` ${config.coin2}`})
              </option>
            ))}
            <option value="">Fix value:</option>
          </select>
        )}
        {!form![constantField] && (
          <input
            type="number"
            aria-label={label}
            min={range.min}
            max={range.max}
            step={range.step}
            value={String(form![valueField])}
            onChange={(e) => set({ [valueField]: e.target.value } as Partial<CoinSettingsForm>)}
          />
        )}
        <span className="portal-input-unit">{unit === '%' ? '%' : config.coin2}</span>
      </span>
    )
  }

  const bidRange = quoteAsset.bid
  const gridRange = quoteAsset.grid
  const autoBuyAsset = form.autoBuyAsset || settingsDefaultAutoBuyAssetLabel(config)

  return (
    <Modal open title={`${config.coin1}${config.coin2} - ${config.exchangeId ?? ''}`} onClose={() => onClose(false)} className="portal-modal-wide">
      <div data-testid="coin-settings-popup">
        <FieldRow label="Config source">
          {config.strategyId ? `Strategy subscription${form.syncState === 1 ? ' (own)' : ''}` : 'Custom config'}
        </FieldRow>
        {config.strategyId && (
          <FieldRow label="Strategy:">
            <Link to={`/strategy/spot/${config.strategyId}`}>
              {strategy ? (strategy.userState === 1 ? strategy.name : strategy.realPublicName) : config.strategyId}
            </Link>
          </FieldRow>
        )}
        {config.groupId && <FieldRow label="Group:">{config.groupId}</FieldRow>}

        <FieldRow label="Trading Mode" hint={`Effective: ${TRADING_MODES[String(config.tradingMode)] ?? String(config.tradingMode ?? '')}`}>
          {own && Number(config.tradingMode) !== -100 && (
            <select
              aria-label="Trading Mode"
              value={String(form.tradingModeValue)}
              onChange={(e) => set({ tradingModeValue: Number(e.target.value) })}
            >
              {possibleModes.map((m) => (
                <option key={m} value={String(m)}>
                  {TRADING_MODES[String(m)] ?? String(m)}
                </option>
              ))}
              {!currentModeListed && (
                <option value={String(form.tradingModeValue)}>
                  {TRADING_MODES[String(form.tradingModeValue)] ?? String(form.tradingModeValue)}
                </option>
              )}
            </select>
          )}
          {!own && (Number(config.tradingMode) === -100 ? 'Stopped' : (TRADING_MODES[String(config.tradingMode)] ?? String(config.tradingMode)))}
        </FieldRow>

        <FieldRow label="Bid" hint={`Effective: ${String(effective.bid ?? '')} ${config.coin2}`}>
          {own ? (
            rawOrConstant('bidConstant', 'bid', effective.bidConstants, config.coin2, bidRange, 'Bid')
          ) : (
            <>
              <span>
                {form.bidConstant ? `${form.bidConstant} - ` : ''}
                {String(effective.bid ?? '')} {config.coin2}
              </span>
              {form.bidConstant &&
                constantOverrideSelect('bidConstants', form.bidConstant, `${form.bidConstant}::${config.coin2}`, ` ${config.coin2}`, bidRange)}
            </>
          )}
        </FieldRow>

        <FieldRow
          label={form.setDifferentGridDown ? 'Grid Up' : 'Grid'}
          hint={`Effective: ${String(effective.gridUp ?? '')}%`}
        >
          {own ? (
            rawOrConstant('gridUpConstant', 'gridUp', effective.gridConstants, '%', gridRange, 'Grid Up')
          ) : (
            <>
              <span>
                {form.gridUpConstant ? `${form.gridUpConstant} - ` : ''}
                {String(effective.gridUp ?? '')}%
              </span>
              {form.gridUpConstant && constantOverrideSelect('gridConstants', form.gridUpConstant, form.gridUpConstant, '%', gridRange)}
            </>
          )}
          {own && (
            <button type="button" onClick={() => set({ setDifferentGridDown: !form.setDifferentGridDown })}>
              {form.setDifferentGridDown ? 'Use same value for Grid Down' : 'Set different value for Grid Down'}
            </button>
          )}
        </FieldRow>
        {form.setDifferentGridDown && (
          <FieldRow label="Grid Down" hint={`Effective: ${String(effective.gridDown ?? '')}%`}>
            {own ? (
              rawOrConstant('gridDownConstant', 'gridDown', effective.gridConstants, '%', gridRange, 'Grid Down')
            ) : (
              <>
                <span>
                  {form.gridDownConstant ? `${form.gridDownConstant} - ` : ''}
                  {String(effective.gridDown ?? '')}%
                </span>
                {form.gridDownConstant &&
                  constantOverrideSelect('gridConstants', form.gridDownConstant, form.gridDownConstant, '%', gridRange)}
              </>
            )}
          </FieldRow>
        )}

        <div className="portal-inline-actions">
          <button type="button" onClick={() => setShowAdvanced((v) => !v)}>
            {showAdvanced ? 'Hide advanced settings' : 'Show advanced settings'}
          </button>
        </div>

        {showAdvanced && (
          <div data-testid="coin-settings-advanced">
            <FieldRow label="Lock Off Trading Mode">
              {!own ? (
                getFieldLabel(effective.lockOnOffMode, '', LOGIC_LABELS)
              ) : (
                <select
                  aria-label="Lock Off Trading Mode"
                  value={String(form.lockOnOffMode)}
                  onChange={(e) => set({ lockOnOffMode: Number(e.target.value) })}
                >
                  <option value="-1">Use my default value ({getFieldLabel(config.default?.lockOnOffMode, '', LOGIC_LABELS)})</option>
                  <option value="0">No</option>
                  <option value="1">Yes</option>
                </select>
              )}
            </FieldRow>

            <FieldRow label="Max MOP count">
              <select
                aria-label="Max MOP count"
                value={String(form.maxMopSelectValue)}
                onChange={(e) => set({ maxMopSelectValue: Number(e.target.value) })}
              >
                {(form.syncState > 0 || form.maxMopSelectValue === -2) && (
                  <option value="-2">Use trader&apos;s value ({getFieldLabel(traderField(config, 'maxMopCount'), '', MAX_MOP_LABELS)})</option>
                )}
                <option value="-1">Use my default value ({getFieldLabel(config.default?.maxMopCount, '', MAX_MOP_LABELS)})</option>
                <option value="0">Unlimited</option>
                <option value="-100">Custom value:</option>
              </select>
              {form.maxMopSelectValue === -100 && (
                <input
                  type="number"
                  aria-label="Max MOP custom value"
                  min={0}
                  max={20}
                  step={1}
                  value={String(form.maxMopCustomValue)}
                  onChange={(e) => set({ maxMopCustomValue: e.target.value })}
                />
              )}
            </FieldRow>

            <FieldRow label="Off handler">
              <select
                aria-label="Off handler"
                value={String(form.offHandlerType)}
                onChange={(e) => {
                  const type = Number(e.target.value)
                  set({ offHandlerType: type, offModeTrigger: offModeTriggerForType(type) })
                }}
              >
                {(form.syncState > 0 || Number(config.offModeTrigger) === -2) && (
                  <option value="-2">
                    Use trader&apos;s value ({getFieldLabel(traderField(config, 'offModeTrigger'), '', OFF_MODE_TRIGGERS)})
                  </option>
                )}
                <option value="-1">Use my default value ({getFieldLabel(config.default?.offModeTrigger, '', OFF_MODE_TRIGGERS)})</option>
                <option value="0">Do nothing</option>
                <option value="1">Sell Open Positions:</option>
                <option value="2">MirrorTrading:</option>
              </select>
              {form.offHandlerType > 0 && (
                <select
                  aria-label="Off handler action"
                  value={String(form.offModeTrigger)}
                  onChange={(e) => set({ offModeTrigger: Number(e.target.value) })}
                >
                  {(form.offHandlerType === 1 ? [1, 2, 3, 4, 5, 6] : [11, 12, 13, 14, 15]).map((t) => (
                    <option key={t} value={String(t)}>
                      {OFF_MODE_TRIGGERS[String(t)]}
                    </option>
                  ))}
                </select>
              )}
            </FieldRow>

            <FieldRow label="Run off handler on non-current strategy MOPS too">
              <select
                aria-label="Run off handler on non-current strategy MOPS too"
                value={String(form.offHandlerRunOnNonSameStrategyPositions)}
                onChange={(e) => set({ offHandlerRunOnNonSameStrategyPositions: Number(e.target.value) })}
              >
                <option value="-1">
                  Use my default value ({getFieldLabel(config.default?.offHandlerRunOnNonSameStrategyPositions, '', LOGIC_LABELS)})
                </option>
                <option value="0">No</option>
                <option value="1">Yes</option>
              </select>
            </FieldRow>

            <FieldRow label="Auto Buy Mode">
              <select aria-label="Auto Buy Mode" value={String(form.autoBuyMode)} onChange={(e) => set({ autoBuyMode: Number(e.target.value) })}>
                <option value="-1">Use my default value ({settingsDefaultAutoBuyModeLabel(config, AUTO_BUY_MODES)})</option>
                {[0, 1, 2].map((m) => (
                  <option key={m} value={String(m)}>
                    {AUTO_BUY_MODES[String(m)]}
                  </option>
                ))}
              </select>
            </FieldRow>

            <FieldRow label="Auto Buy Amount">
              {isUsd(autoBuyAsset) ? (
                <>
                  <select
                    aria-label="Auto Buy USD Amount source"
                    value={String(form.autoBuyUsdAmountSelectValue)}
                    onChange={(e) => set({ autoBuyUsdAmountSelectValue: Number(e.target.value) })}
                  >
                    <option value="-1">Use my default value ({settingsDefaultAutoBuyAmountLabel(config)})</option>
                    <option value="-100">Custom value:</option>
                  </select>
                  {form.autoBuyUsdAmountSelectValue === -100 && (
                    <span className="portal-input-group">
                      <input
                        type="number"
                        aria-label="Auto Buy USD Amount"
                        min={0}
                        max={1000}
                        step={1}
                        value={String(form.autoBuyUsdAmountCustomValue)}
                        onChange={(e) => set({ autoBuyUsdAmountCustomValue: e.target.value })}
                      />
                      <span className="portal-input-unit">{PRIMARY_USD_ASSET}</span>
                    </span>
                  )}
                </>
              ) : (
                <>
                  <select
                    aria-label="Auto Buy Amount source"
                    value={String(form.autoBuyAmountSelectValue)}
                    onChange={(e) => set({ autoBuyAmountSelectValue: Number(e.target.value) })}
                  >
                    <option value="-1">Use my default value ({settingsDefaultAutoBuyAmountLabel(config)})</option>
                    <option value="-100">Custom value:</option>
                  </select>
                  {form.autoBuyAmountSelectValue === -100 && (
                    <span className="portal-input-group">
                      {/* legacy quirk kept: the coin-denominated amount uses the GRID range */}
                      <input
                        type="number"
                        aria-label="Auto Buy Amount"
                        min={gridRange.min}
                        max={gridRange.max}
                        step={gridRange.step}
                        value={String(form.autoBuyAmountCustomValue)}
                        onChange={(e) => set({ autoBuyAmountCustomValue: e.target.value })}
                      />
                      <span className="portal-input-unit">{config.coin2}</span>
                    </span>
                  )}
                </>
              )}
            </FieldRow>

            <FieldRow label="Auto Buy Asset">
              <select aria-label="Auto Buy Asset" value={form.autoBuyAsset} onChange={(e) => set({ autoBuyAsset: e.target.value })}>
                <option value="">Use my default value ({settingsDefaultAutoBuyAssetLabel(config)})</option>
                {USD_ASSETS.map((a) => (
                  <option key={a} value={a}>
                    {a}
                  </option>
                ))}
                {!isUsd(config.coin2) && <option value={config.coin2}>{config.coin2}</option>}
              </select>
            </FieldRow>

            <h3 className="portal-section-title">Custom bid constants</h3>
            {form.bidConstantNames.map((name) => (
              <FieldRow key={`bid-${name}`} label={name}>
                {constantOverrideSelect('bidConstants', name, `${name}::${config.coin2}`, ` ${config.coin2}`, bidRange)}
                <button
                  type="button"
                  className="portal-button-danger"
                  onClick={() =>
                    set({
                      bidConstantNames: form.bidConstantNames.filter((n) => n !== name),
                      bidConstants: { ...form.bidConstants, [name]: { value: -1, customValue: 0 } },
                    })
                  }
                >
                  Remove
                </button>
              </FieldRow>
            ))}
            <div className="portal-inline-actions">
              <input
                type="text"
                aria-label="New custom bid constant name"
                placeholder="add new bid constant"
                value={newBid.name}
                onChange={(e) => setNewBid({ ...newBid, name: e.target.value })}
              />
              <input
                type="number"
                aria-label="New custom bid constant value"
                value={newBid.value}
                onChange={(e) => setNewBid({ ...newBid, value: Number(e.target.value) })}
              />
              <button
                type="button"
                onClick={() => {
                  const next = addConstantOverride(form.bidConstantNames, form.bidConstants, newBid.name, newBid.value)
                  if (!next) return
                  set({ bidConstantNames: next.names, bidConstants: next.map })
                  setNewBid({ name: '', value: 0 })
                }}
              >
                Add
              </button>
            </div>

            <h3 className="portal-section-title">Custom grid constants</h3>
            {form.gridConstantNames.map((name) => (
              <FieldRow key={`grid-${name}`} label={name}>
                {constantOverrideSelect('gridConstants', name, name, '%', gridRange)}
                <button
                  type="button"
                  className="portal-button-danger"
                  onClick={() =>
                    set({
                      gridConstantNames: form.gridConstantNames.filter((n) => n !== name),
                      gridConstants: { ...form.gridConstants, [name]: { value: -1, customValue: 0 } },
                    })
                  }
                >
                  Remove
                </button>
              </FieldRow>
            ))}
            <div className="portal-inline-actions">
              <input
                type="text"
                aria-label="New custom grid constant name"
                placeholder="add new grid constant"
                value={newGrid.name}
                onChange={(e) => setNewGrid({ ...newGrid, name: e.target.value })}
              />
              <input
                type="number"
                aria-label="New custom grid constant value"
                value={newGrid.value}
                onChange={(e) => setNewGrid({ ...newGrid, value: Number(e.target.value) })}
              />
              <button
                type="button"
                onClick={() => {
                  const next = addConstantOverride(form.gridConstantNames, form.gridConstants, newGrid.name, newGrid.value)
                  if (!next) return
                  set({ gridConstantNames: next.names, gridConstants: next.map })
                  setNewGrid({ name: '', value: 0 })
                }}
              >
                Add
              </button>
            </div>
          </div>
        )}

        <div className="portal-form-footer portal-inline-actions">
          <button type="button" onClick={() => onClose(false)}>
            Close
          </button>
          <button type="submit" onClick={save} disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </Modal>
  )
}
